import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Empty,
  Row,
  Select,
  Table,
  Tag,
  notification,
} from "antd";
import { EyeFilled, RedoOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useLoading } from "../../context/loadingContext";
import GetMerchantSettle from "../../functions/merchant/getMerchantSettle";
import moneyFormat from "../../functions/utils/moneyFormat";
import SettleTransactions from "../modal/settleTransactions";
import TablePagination from "components/pagination/tablePagination";
import { bg_button, bg_shadow } from "constant/css";
import { getMerchantId } from "service/storage";
import { buildMerchantSettleListQuery } from "functions/merchant/merchantSettleListQuery";
import MerchantSettleListScopeSelect from "./MerchantSettleListScopeSelect";
import { SETTLE_LIST_SCOPE } from "functions/merchant/merchantSettleListConstants";

const { RangePicker } = DatePicker;

const TxCounts = ({ settled, pending, onPendingClick }) => (
  <span
    className="expense-tx-counts"
    title={`Шилжүүлсэн ${settled}, үлдсэн ${pending}`}
  >
    <span className={settled ? "expense-tx-ok" : "expense-tx-zero"}>{settled}</span>
    <span
      className={
        pending
          ? `expense-tx-wait${onPendingClick ? " cursor-pointer" : ""}`
          : "expense-tx-zero"
      }
      onClick={onPendingClick}
      role={onPendingClick && pending ? "button" : undefined}
    >
      {pending}
    </span>
  </span>
);

/**
 * Shared merchantSettle list + SettleTransactions modal.
 *
 * @param {"admin"|"merchant"} mode
 * @param {Array<{value:number,label:string}>} [merchantOptions] - admin mode
 * @param {boolean} [active=true] - skip fetch when false (admin tab)
 * @param {string} [tableScrollY]
 * @param {number} [defaultPageSize]
 */
const MerchantSettleListPanel = ({
  mode = "merchant",
  merchantOptions = [],
  active = true,
  tableScrollY = "calc(100vh - 38vh)",
  defaultPageSize = 20,
}) => {
  const isAdmin = mode === "admin";
  const { showLoading, hideLoading } = useLoading();

  const fixedMerchantId = useMemo(
    () => (isAdmin ? null : getMerchantId()),
    [isAdmin],
  );

  const [selectedMerchantId, setSelectedMerchantId] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [settleListScope, setSettleListScope] = useState(
    SETTLE_LIST_SCOPE.ALL,
  );
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [paginationData, setPaginationData] = useState({
    pageSize: defaultPageSize,
    total: 0,
  });
  const [showTransactions, setShowTransactions] = useState(null);
  const [settleSettledAt, setSettleSettledAt] = useState(null);
  const [modalInitialStatus, setModalInitialStatus] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [totals, setTotals] = useState({
    totalAmount: 0,
    totalMerchant: 0,
    totalXpay: 0,
    totalSubMerchant: 0,
    totalProcessingAmount: 0,
    totalFailed: 0,
    totalSuccess: 0,
  });

  const effectiveMerchantId = isAdmin ? selectedMerchantId : fixedMerchantId;

  const openModal = (row, initialStatus = null) => {
    setSettleSettledAt(row?.settledAt || null);
    setModalInitialStatus(initialStatus);
    setShowTransactions(row?.id || null);
  };

  const closeModal = () => {
    setShowTransactions(null);
    setSettleSettledAt(null);
    setModalInitialStatus(null);
  };

  const refreshList = () => {
    setRefreshKey((v) => v + 1);
  };

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    const load = async () => {
      if (!effectiveMerchantId) {
        setData([]);
        setPaginationData({ pageSize, total: 0 });
        setTotals({
          totalAmount: 0,
          totalMerchant: 0,
          totalXpay: 0,
          totalSubMerchant: 0,
          totalProcessingAmount: 0,
          totalFailed: 0,
          totalSuccess: 0,
        });
        return;
      }

      showLoading();
      try {
        const query = buildMerchantSettleListQuery({
          page,
          pageSize,
          createdAt,
          settleListScope,
        });
        const response = await GetMerchantSettle(effectiveMerchantId, query);
        if (cancelled || !response) return;

        setData(response.data || []);
        setPaginationData(response.pagination || { pageSize, total: 0 });
        setTotals({
          totalAmount: response.totalAmount || 0,
          totalMerchant: response.totalMerchant || 0,
          totalXpay: response.totalXpay || 0,
          totalSubMerchant: response.totalSubMerchantAmount || 0,
          totalProcessingAmount: response.totalProcessingAmount || 0,
          totalFailed: response.aggregate?.unsettledTxCount ?? 0,
          totalSuccess: response.aggregate?.settledTxCount ?? 0,
        });
      } catch (err) {
        if (!cancelled) {
          setData([]);
          notification.error({
            message:
              err.response?.data?.message ||
              err.message ||
              "Өндөрлөлтийн жагсаалт авахад алдаа гарлаа",
          });
        }
      } finally {
        hideLoading();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    effectiveMerchantId,
    createdAt,
    page,
    pageSize,
    settleListScope,
    refreshKey,
  ]);

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 70,
    },
    {
      title: "Нийт дүн",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (value) => moneyFormat(value),
    },
    {
      title: "Тоо/ш",
      dataIndex: "count",
      key: "count",
      align: "right",
    },
    {
      title: "Дэд мерчант дүн",
      dataIndex: "subMerchantAmount",
      key: "subMerchantAmount",
      align: "right",
      render: (value) => moneyFormat(value),
    },
    {
      title: "Мерчант дүн",
      dataIndex: "merchantAmount",
      key: "merchantAmount",
      align: "right",
      render: (value) => moneyFormat(value),
    },
    {
      title: "xPay авах дүн",
      dataIndex: "xpayAmount",
      key: "xpayAmount",
      align: "right",
      render: (value) => moneyFormat(value),
    },
    {
      title: "Процэсс дүн",
      dataIndex: "processingAmount",
      key: "processingAmount",
      align: "right",
      render: (value) => (
        <span className="text-orange-700">{moneyFormat(value)}</span>
      ),
    },
    {
      title: "Үүссэн огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Төрөл",
      dataIndex: "settledType",
      key: "settledType",
      render: (value) => value || "-",
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      key: "status",
      render: (value) =>
        value === "SETTLED" ? (
          <Tag color="cyan">Өндөрлөсөн</Tag>
        ) : value === "ACTIVE" ? (
          <Tag color="blue">Идэвхтэй</Tag>
        ) : (
          <Tag>Идэвхгүй</Tag>
        ),
    },
    {
      title: (
        <span className="expense-tx-head">
          Гүйлгээ
          <span className="expense-tx-head__meta">
            <span>шилжүүлсэн</span>
            <span>үлдсэн</span>
          </span>
        </span>
      ),
      key: "txCounts",
      width: 148,
      align: "right",
      render: (_, row) => (
        <TxCounts
          settled={row.settledCount ?? 0}
          pending={row.unsettledCount ?? 0}
          onPendingClick={
            (row.unsettledCount ?? 0) > 0
              ? () => openModal(row, "PENDING")
              : undefined
          }
        />
      ),
    },
    {
      title: "",
      key: "action",
      fixed: "right",
      width: 40,
      render: (_, row) => (
        <Button
          size="small"
          onClick={() => openModal(row, null)}
          icon={<EyeFilled style={{ color: "green", fontSize: 14 }} />}
        />
      ),
    },
  ];

  return (
    <div>
      <Row
        className="invoice-control-search mb-2 flex-wrap"
        align="middle"
        style={{ gap: 10 }}
      >
        <Button
          type="primary"
          className={`${bg_button} ${bg_shadow} rounded-xl`}
          icon={<RedoOutlined />}
          onClick={() => {
            setCreatedAt(null);
            setSettleListScope(SETTLE_LIST_SCOPE.ALL);
            setPage(1);
            refreshList();
          }}
        />
        <MerchantSettleListScopeSelect
          value={settleListScope}
          onChange={(scope) => {
            setSettleListScope(scope);
            setPage(1);
          }}
        />
        <RangePicker
          size="small"
          value={createdAt}
          onChange={(dates) => {
            setPage(1);
            setCreatedAt(dates?.[0] && dates?.[1] ? dates : null);
          }}
          allowClear
          placeholder={["Үүссэн огноо", "Дуусах"]}
        />
        {isAdmin ? (
          <Select
            size="small"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Бизнесийн нэр"
            style={{ width: 220 }}
            value={selectedMerchantId || undefined}
            onChange={(value) => {
              setPage(1);
              setSelectedMerchantId(value || null);
            }}
            options={merchantOptions}
          />
        ) : null}
      </Row>

      {isAdmin && !effectiveMerchantId ? (
        <div className="tableContainer">
          <Empty description="Бизнес сонгоно уу" />
        </div>
      ) : (
        <div className="tableContainer">
          <Table
            className="invoice-control-table expense-settle-table"
            size="small"
            columns={columns}
            rowKey={(record) => record.id}
            scroll={{ x: "max-content", y: tableScrollY }}
            pagination={false}
            dataSource={data}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row className="summaryRow">
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1} align="right">
                    <span className="expense-total">
                      {moneyFormat(totals.totalAmount)}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align="right">
                    {moneyFormat(totals.totalSubMerchant)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    {moneyFormat(totals.totalMerchant)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    {moneyFormat(totals.totalXpay)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <span className="expense-processing">
                      {moneyFormat(totals.totalProcessingAmount)}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} />
                  <Table.Summary.Cell index={8} />
                  <Table.Summary.Cell index={9} />
                  <Table.Summary.Cell
                    index={10}
                    align="right"
                    className="expense-tx-summary"
                  >
                    <TxCounts
                      settled={totals.totalSuccess}
                      pending={totals.totalFailed}
                    />
                    <div className="text-[10px] text-gray-400 text-right mt-0.5">
                      шүүлтийн нийт
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          <TablePagination
            page={page}
            paginationData={paginationData}
            changePage={setPage}
            changeSize={(_, size) => {
              setPage(1);
              setPageSize(size);
            }}
          />
        </div>
      )}

      {showTransactions && effectiveMerchantId ? (
        <SettleTransactions
          open={showTransactions}
          refresh={refreshList}
          close={closeModal}
          defaultMerchantId={effectiveMerchantId}
          settledAt={settleSettledAt}
          initialStatus={modalInitialStatus}
        />
      ) : null}
    </div>
  );
};

export default MerchantSettleListPanel;
